import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

const ApexGraph = ({
  labels = [],
  datasets = [],
  statLines = [],
  title = "",
  chartType = "line",
  valueFormatter,
  id = "",
  height = 350,
  showBrush = true,
  dateRange,
  rawLabels = [],
  mode,
  metricUnit = "",
}) => {
  const parseDateSafe = (dateStr) => {
    if (!dateStr) return NaN;
    if (dateStr instanceof Date) return dateStr.getTime();
    if (typeof dateStr !== "string") return new Date(dateStr).getTime();

    // Parse YYYY-MM-DD HH:mm:ss format as naive local time to avoid timezone shifts
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      const hour = parseInt(match[4] || "0", 10);
      const minute = parseInt(match[5] || "0", 10);
      const second = parseInt(match[6] || "0", 10);
      return new Date(year, month, day, hour, minute, second).getTime();
    }
    return new Date(dateStr).getTime();
  };

  const uniqueId = useMemo(
    () => id || `apex-${Math.random().toString(36).substring(2, 9)}`,
    [id],
  );
  const xLabels = rawLabels.length ? rawLabels : labels;
  const isBar = chartType === "bar";

  const series = useMemo(() => {
    return datasets
      .map((ds) => {
        const mappedData = ds.data
          .map((val, idx) => {
            const rawX = xLabels[idx];
            if (rawX === undefined || rawX === null) return null;

            const x = parseDateSafe(rawX);
            if (!Number.isFinite(x)) return null;

            let yVal = val;
            if (typeof yVal === "string") {
              yVal = yVal.trim() === "" ? null : Number(yVal);
            } else if (yVal !== null && yVal !== undefined) {
              yVal = Number(yVal);
            }

            return {
              x,
              y: Number.isFinite(yVal) ? yVal : null,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.x - b.x);

        const deduplicated = [];
        for (const point of mappedData) {
          if (
            deduplicated.length > 0 &&
            point.x === deduplicated[deduplicated.length - 1].x
          ) {
            deduplicated[deduplicated.length - 1] = point;
          } else {
            deduplicated.push(point);
          }
        }

        return {
          name: ds.label,
          data: deduplicated,
          color: ds.borderColor,
          type: isBar ? "bar" : "area",
        };
      })
      .filter((dataset) => dataset.data.length > 0);
  }, [datasets, xLabels, isBar]);

  const statAnnotations = useMemo(() => {
    if (isBar || !statLines.length) return [];

    return statLines
      .map((statLine) => {
        const value = Number(statLine?.value);
        if (!Number.isFinite(value)) return null;

        return {
          y: value,
          borderColor: statLine.color,
          strokeDashArray: statLine.dashArray ?? 8,
          borderWidth: 3,
          label: {
            text: statLine.label,
            style: {
              color: statLine.color,
              background: "#fff",
              fontWeight: 700,
            },
          },
        };
      })
      .filter(Boolean);
  }, [isBar, statLines]);

  const statTooltipRows = useMemo(() => {
    return statLines
      .map((statLine) => {
        const value = Number(statLine?.value);
        if (!Number.isFinite(value)) return null;

        return {
          label: statLine.label,
          value,
          color: statLine.color,
        };
      })
      .filter(Boolean);
  }, [statLines]);

  const mainOptions = useMemo(
    () => ({
      chart: {
        id: uniqueId,
        type: isBar ? "bar" : "area",
        height,
        toolbar: {
          autoSelected: "zoom",
          show: false,
        },
        zoom: {
          enabled: true,
        },
        animations: {
          enabled: false,
        },
      },
      stroke: {
        curve: "smooth",
        width: isBar ? 0 : 2,
      },
      fill: {
        type: isBar ? "solid" : "gradient",
        opacity: isBar ? 1 : 0.25,
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.1,
          stops: [0, 90, 100],
        },
      },
      dataLabels: {
        enabled: false,
      },
      annotations: {
        yaxis: statAnnotations,
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          style: {
            fontSize: "10px",
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) =>
            typeof val === "number"
              ? valueFormatter
                ? valueFormatter(val)
                : val.toFixed(2)
              : val,
          style: {
            fontSize: "10px",
          },
        },
      },
      tooltip: {
        x: {
          format:
            mode === "second" ? "dd MMM yyyy HH:mm:ss" : "dd MMM yyyy HH:mm",
        },
        shared: true,
        intersect: false,
        custom: ({ series, dataPointIndex, w }) => {
          let tooltipContent = `<div style="padding: 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 4px; min-width: 180px;">`;

          for (let i = 0; i < w.config.series.length; i++) {
            const value = series[i] ? series[i][dataPointIndex] : null;
            if (value !== undefined && value !== null) {
              const seriesName = String(w.config.series[i]?.name || "");
              const formattedValue = typeof value === "number"
                ? valueFormatter
                  ? valueFormatter(value)
                  : value.toFixed(2)
                : value;
              tooltipContent += `<div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 6px;"><span style="font-weight: 600; color: #0f172a;">${seriesName}</span><span style="color: #0f172a;">${formattedValue}</span></div>`;
            }
          }

          if (statTooltipRows.length > 0) {
            tooltipContent += `<div style="height: 1px; background: #e2e8f0; margin: 8px 0;"></div>`;

            statTooltipRows.forEach((row) => {
              const formattedValue = typeof row.value === "number"
                ? valueFormatter
                  ? valueFormatter(row.value)
                  : row.value.toFixed(2)
                : row.value;

              tooltipContent += `<div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 4px;"><span style="font-weight: 700; color: ${row.color};">${row.label}</span><span style="font-weight: 700; color: ${row.color};">${formattedValue}</span></div>`;
            });
          }

          tooltipContent += `</div>`;
          return tooltipContent;
        },
      },
      legend: {
        show: false,
        position: "top",
        horizontalAlign: "right",
        fontSize: "11px",
        fontWeight: 600,
      },
      grid: {
        borderColor: "#f1f5f9",
      },
      title: {
        text: title,
        align: "left",
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          color: "#1e293b",
        },
      },
    }),
    [height, isBar, mode, statAnnotations, statTooltipRows, title, uniqueId, valueFormatter],
  );

  const brushOptions = useMemo(
    () => ({
      chart: {
        id: `${uniqueId}-brush`,
        height: 100,
        type: "area",
        brush: {
          target: uniqueId,
          enabled: true,
        },
        selection: {
          enabled: true,
        },
      },
      colors: ["#64748b"],
      fill: {
        type: "gradient",
        gradient: {
          opacityFrom: 0.4,
          opacityTo: 0.1,
        },
      },
      xaxis: {
        type: "datetime",
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        tickAmount: 2,
        labels: {
          show: false,
        },
      },
    }),
    [uniqueId],
  );

  if (series.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height,
          color: "#64748b",
          border: "1px dashed #e2e8f0",
          borderRadius: "12px",
        }}
      >
        No valid data points found
      </div>
    );
  }

  return (
    <div className="apex-graph-wrapper" style={{ width: "100%" }}>
      <div id="chart-main">
        <ReactApexChart
          key={`${uniqueId}-${mode}-${metricUnit}`}
          options={mainOptions}
          series={series}
          type={isBar ? "bar" : "area"}
          height={height}
        />
      </div>
      {showBrush && series.length > 0 && series[0].data.length > 2 && (
        <div id="chart-brush" style={{ marginTop: -20 }}>
          <ReactApexChart
            key={`${uniqueId}-brush-chart`}
            options={brushOptions}
            series={[
              {
                name: "Selection",
                data: series[0].data,
              },
            ]}
            type="area"
            height={100}
          />
        </div>
      )}
    </div>
  );
};

export default ApexGraph;
